import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, User, Loader2 } from 'lucide-react';
import { usePageHeader } from '../contexts/PageHeaderContext';
import PageHeader from '../components/PageHeader';
import { showToast } from '../utils/toast';

// Page Header configuration
const PAGE_HEADER_CONFIG = {
  breadcrumbs: [
    { label: 'Dashboard', path: '/' },
    { label: 'AI Chatbot' }
  ],
  title: 'AI Chatbot',
  icon: '💬',
  description: 'AI-powered assistant for answering your questions and providing insights.',
};

// Role configuration
const ROLES = [
  { id: 'executive', label: 'Executive' },
  { id: 'manager', label: 'Manager' },
  { id: 'analyst', label: 'Analyst' },
  { id: 'general', label: 'General' },
];

// Suggested questions by role
const SUGGESTED_QUESTIONS = {
  executive: [
    'What is the current project status?',
    'Show me the key performance metrics',
    'What are the main risks we should be aware of?',
    'Give me a summary of recent activities',
  ],
  manager: [
    'What tasks need my attention?',
    'Show team performance this week',
    'What are the upcoming deadlines?',
    'List active projects and their status',
  ],
  analyst: [
    'Show me the latest data trends',
    'What insights can you provide from the data?',
    'Compare performance across different periods',
    'Generate a detailed analysis report',
  ],
  general: [
    'How can I get started?',
    'What features are available?',
    'Show me a quick overview',
    'Help me understand the system',
  ],
};

// Message component
const MessageBubble = ({ message, isUser }) => {
  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="flex items-start max-w-[80%]">
          <div 
            className="px-5 py-3 rounded-2xl rounded-tr-md shadow-sm text-white"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm leading-relaxed">{message.content}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start mb-4">
      <div className="flex items-start max-w-[80%]">
        <div 
          className="w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
          style={{ backgroundColor: 'var(--color-primary-light)' }}
        >
          <MessageCircle className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
        </div>
        <div 
          className="px-5 py-3 rounded-2xl rounded-tl-md shadow-sm"
          style={{ 
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)'
          }}
        >
          <p 
            className="text-sm leading-relaxed whitespace-pre-wrap"
            style={{ color: 'var(--color-text)' }}
          >{message.content}</p>
        </div>
      </div>
    </div>
  );
};

// Loading indicator component
const LoadingIndicator = () => (
  <div className="flex justify-start mb-4">
    <div className="flex items-start">
      <div 
        className="w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 animate-pulse"
        style={{ backgroundColor: 'var(--color-primary-light)' }}
      >
        <MessageCircle className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
      </div>
      <div 
        className="px-5 py-4 rounded-2xl rounded-tl-md shadow-sm"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-primary)' }} />
          <span 
            className="text-sm"
            style={{ color: 'var(--color-text-secondary)' }}
          >Analyzing your question...</span>
        </div>
        <div 
          className="mt-3 h-1 w-48 rounded-full overflow-hidden"
          style={{ backgroundColor: 'var(--color-border)' }}
        >
          <div 
            className="h-full rounded-full animate-pulse" 
            style={{ 
              width: '70%',
              backgroundColor: 'var(--color-primary)' 
            }} 
          />
        </div>
      </div>
    </div>
  </div>
);

// Main page component
const TemplateChatbot = () => {
  // Set page header configuration
  usePageHeader(PAGE_HEADER_CONFIG);

  const [selectedRole, setSelectedRole] = useState('general');
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Send message
  const handleSendMessage = async (text) => {
    const messageText = text || inputText.trim();
    if (!messageText || isLoading) return;

    // Add user message
    const userMessage = { id: Date.now(), content: messageText, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    // Simulate AI response (in real project, this would call API)
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        content: `Thank you for your question about "${messageText}". I'm processing your request and will provide you with relevant insights. This is a demo response - in production, this would connect to your backend API for real-time analysis.`,
        isUser: false,
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsLoading(false);
      showToast('Response received', 'success');
    }, 2000);
  };

  // Click suggested question
  const handleSuggestedQuestion = (question) => {
    handleSendMessage(question);
  };

  // Handle keyboard events
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Show suggestions when no messages
  const showSuggestions = messages.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader {...PAGE_HEADER_CONFIG} />
      
      {/* Chat area */}
      <div 
        className="flex flex-col h-[calc(100vh-300px)] rounded-2xl overflow-hidden transition-colors duration-300"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        {/* Message list / Suggested questions */}
        <div className="flex-1 overflow-y-auto p-6">
          {showSuggestions ? (
            // Suggested questions interface
            <div className="flex flex-col items-center justify-center h-full">
              {/* Role selection */}
              <div className="text-center mb-8">
                <p 
                  className="mb-4"
                  style={{ color: 'var(--color-text-secondary)' }}
                >Select your role for tailored questions:</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {ROLES.map((role) => (
                    <button
                      key={role.id}
                      onClick={() => setSelectedRole(role.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedRole === role.id
                          ? 'text-white shadow-md'
                          : ''
                      }`}
                      style={selectedRole === role.id 
                        ? { backgroundColor: 'var(--color-primary)' }
                        : { 
                            backgroundColor: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            color: 'var(--color-text)'
                          }
                      }
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Suggested questions list */}
              <div className="w-full max-w-2xl">
                <h3 
                  className="text-center font-semibold mb-4"
                  style={{ color: 'var(--color-text)' }}
                >
                  Suggested Questions for {ROLES.find(r => r.id === selectedRole)?.label}
                </h3>
                <div className="space-y-3">
                  {SUGGESTED_QUESTIONS[selectedRole]?.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="w-full text-left px-5 py-3.5 rounded-xl text-sm transition-all duration-200 hover:shadow-sm"
                      style={{ 
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text)'
                      }}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            // Chat message list
            <div className="space-y-2">
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} isUser={message.isUser} />
              ))}
              {isLoading && <LoadingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        <div 
          className="p-4"
          style={{ 
            borderTop: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-background)'
          }}
        >
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                messages.length > 0
                  ? "Ask a question..."
                  : "Type your question here..."
              }
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all disabled:cursor-not-allowed disabled:opacity-50"
              style={{ 
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text)',
                focusRingColor: 'var(--color-primary)'
              }}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputText.trim() || isLoading}
              className="px-5 py-3 text-white font-medium rounded-xl transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              <span>Send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateChatbot;
